// Propose a trade: pick an opposing team, check players on both sides,
// optional message. Scrollable dialog with pinned header/footer (create-league
// modal pattern). Panels stack on mobile.
import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { RosterSlot, Team } from '../../types';
import { seasonAverage } from '../../utils/playerSeason';

interface ProposeTradeModalProps {
  leagueId: number;
  myTeam: Team;
  teams: Team[];
  onClose: () => void;
  onProposed: () => void;
}

function RosterChecklist({
  title, slots, selected, onToggle,
}: {
  title: string;
  slots: RosterSlot[];
  selected: Set<number>;
  onToggle: (playerId: number) => void;
}) {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-2">{title}</p>
      <div className="border border-secondary-200 divide-y divide-secondary-200">
        {slots.length === 0 && (
          <p className="px-3 py-3 text-sm text-secondary-400">No players on this roster.</p>
        )}
        {slots.map((slot) => {
          const p = slot.player;
          if (!p) return null;
          const checked = selected.has(p.id);
          const season = seasonAverage(p);
          return (
            <label
              key={slot.id}
              className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-secondary-50 ${
                checked ? 'bg-gold/10' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(p.id)}
                className="flex-shrink-0"
              />
              <span className="text-sm font-medium text-ink-900 truncate">{p.name}</span>
              <span
                className={`px-1.5 py-0.5 text-[0.65rem] font-display leading-none ${
                  p.position === 'K' ? 'bg-ink-900 text-gold' : 'bg-success text-white'
                }`}
              >
                {p.position}
              </span>
              <span className="ml-auto text-right leading-tight">
                <span className="block font-display text-sm text-secondary-500 tabular-nums">{season.display}</span>
                <span className="block text-[0.55rem] uppercase tracking-wider text-secondary-400">{season.label}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function ProposeTradeModal({
  leagueId, myTeam, teams, onClose, onProposed,
}: ProposeTradeModalProps) {
  const otherTeams = teams.filter((t) => t.id !== myTeam.id);
  const [recipientId, setRecipientId] = useState<number | ''>('');
  const [myRoster, setMyRoster] = useState<RosterSlot[]>([]);
  const [theirRoster, setTheirRoster] = useState<RosterSlot[]>([]);
  const [offered, setOffered] = useState<Set<number>>(new Set());
  const [requested, setRequested] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (recipientId === '') return;
    let cancelled = false;
    setOffered(new Set());
    setRequested(new Set());
    setError(null);
    (async () => {
      try {
        const [mine, theirs] = await Promise.all([
          apiService.getRoster(myTeam.id),
          apiService.getRoster(Number(recipientId)),
        ]);
        if (cancelled) return;
        setMyRoster(mine);
        setTheirRoster(theirs);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load rosters');
      }
    })();
    return () => { cancelled = true; };
  }, [recipientId, myTeam.id]);

  const toggle = (set: Set<number>, setter: (s: Set<number>) => void) => (playerId: number) => {
    const next = new Set(set);
    if (next.has(playerId)) next.delete(playerId);
    else next.add(playerId);
    setter(next);
  };

  const canSubmit = recipientId !== '' && offered.size > 0 && requested.size > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiService.proposeTrade(leagueId, {
        recipient_team_id: Number(recipientId),
        offered_player_ids: Array.from(offered),
        requested_player_ids: Array.from(requested),
        message: message.trim() || undefined,
      });
      onProposed();
    } catch (e: any) {
      setError(e.message || 'Failed to propose trade');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-ink-900/60" onClick={onClose} />
      <div className="relative bg-white border border-ink-900 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-900 flex-shrink-0">
          <h2 className="font-display text-xl uppercase tracking-tight text-ink-900">Propose Trade</h2>
          <button onClick={onClose} aria-label="Close" className="icon-btn p-2 text-secondary-400 hover:text-ink-900">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label htmlFor="trade-with" className="block text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1.5">
              Trade with
            </label>
            <select
              id="trade-with"
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value === '' ? '' : Number(e.target.value))}
              className="form-select w-full"
            >
              <option value="">Select a team…</option>
              {otherTeams.map((t) => (
                <option key={t.id} value={t.id}>{t.team_name}</option>
              ))}
            </select>
          </div>

          {recipientId !== '' && (
            <>
              <div className="flex flex-col sm:flex-row gap-4">
                <RosterChecklist
                  title="You send"
                  slots={myRoster}
                  selected={offered}
                  onToggle={toggle(offered, setOffered)}
                />
                <RosterChecklist
                  title="You receive"
                  slots={theirRoster}
                  selected={requested}
                  onToggle={toggle(requested, setRequested)}
                />
              </div>

              <div>
                <label htmlFor="trade-message" className="block text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1.5">
                  Message (optional)
                </label>
                <textarea
                  id="trade-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                  className="form-input w-full"
                  placeholder="Make your case…"
                />
              </div>
            </>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-secondary-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-secondary-300 text-secondary-600 hover:border-ink-900 hover:text-ink-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-ink-900 text-paper hover:bg-ink-900/90 disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Send Offer'}
          </button>
        </div>
      </div>
    </div>
  );
}
